import React from "react";
import {ComposedChart,
    Cell,
    Bar,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer, BarChart, CartesianGrid, Legend} from 'recharts';
import { Button, Card, CardBody } from 'reactstrap';
import CardHeader from 'reactstrap/lib/CardHeader';
import { css } from "aphrodite/no-important";
import { styles } from "src/components/charts/CombinedTrendChartStyles";
import {HorizontalAxisTick, VerticalAxisTick} from 'src/components/charts/AxisTick';
import { CustomToolTip } from "src/components/charts/CustomTooltip";
import { RequestState } from "src/models/RequestState";
import { Api } from "src/core/api";
import axios from "axios";
import { JsonConvert, JsonObject, JsonProperty } from "json2typescript";
import { CombinedToolTip } from "../CombinedTooltip";
import millify from "millify";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faFileImage, faQuestionCircle, faExpand } from '@fortawesome/free-solid-svg-icons'
import domtoimage from 'dom-to-image';
import {json2csv} from 'json-2-csv';
import fileDownload from 'js-file-download'
import { URLBuilder } from "src/core/URLBuilder";
import { YearPicker } from "../YearPicker";

interface ICombinedTrendChartProps {
    author_id: string,
    startYear: number,
    endYear: number,
    onRangeChanged?: (startYear: number, endYear: number)=>void;
}

@JsonObject("AuthorCombinedTrendItem")
export class AuthorCombinedTrendChartItem {

    @JsonProperty("year", Number)
    public year: number = 0;

    @JsonProperty("awards_pi", Number)
    public awards_pi: number = 0;

    @JsonProperty("awards_total", Number)
    public awards_total: number = 0;

    @JsonProperty("awards_count", Number)
    public awards_count: number = 0;

    @JsonProperty("proposals_pi", Number)
    public proposals_pi: number = 0;

    @JsonProperty("proposals_total", Number)
    public proposals_total: number = 0;

    @JsonProperty("proposals_count", Number)
    public proposals_count: number = 0;

    @JsonProperty("expenditures", Number)
    public expenditures: number = 0;
}

class CombindedTrendChartState {
    public readonly requestState: RequestState;
    public readonly data: AuthorCombinedTrendChartItem[];
    public readonly error: string;
    public readonly startYear: number;
    public readonly endYear: number;
    public readonly isStartYearDialogVisible: boolean = false;
    public readonly isEndYearDialogVisible: boolean = false;

    constructor() {
        this.requestState = RequestState.NOT_ASKED;
        this.data = [];
        this.error = "";
        const date = new Date();
        this.startYear = date.getFullYear() - 5;
        this.endYear = date.getFullYear();
    }
}

export class CombinedTrendChart extends React.Component<ICombinedTrendChartProps, CombindedTrendChartState> {
    
    constructor(props: ICombinedTrendChartProps){
        super(props);
        this.state = new CombindedTrendChartState();
    }
    
    public async componentDidMount () {
        this.fetchData(this.state.startYear, this.state.endYear);   
    }

    public fetchData = (startYear: number, endYear: number) => {
        const id = this.props.author_id;
        const url = URLBuilder.author_combined_trend(id, startYear, endYear);
        axios.get(url).then((res)=> {
            if(res.status === 200){
                try{
                    const jsonConvert = new JsonConvert();
                    const data = jsonConvert.deserialize(res.data, AuthorCombinedTrendChartItem);
                    const dataSorted = data.sort((item1:AuthorCombinedTrendChartItem, item2:AuthorCombinedTrendChartItem)=>{return (item1.year as any) - (item2.year as any)})
                    const newState = {...this.state, requestState: RequestState.SUCCESS, data: dataSorted}
                    this.setState(newState)
                }catch(error) {
                    const newState = {...this.state, requestState: RequestState.ERROR, error: error.toString()}
                    this.setState(newState)
                }
            }else{
                const error = res.statusText + ":" + res.data
                const newState = {...this.state, requestState: RequestState.ERROR, error: error}
                this.setState(newState)
            }    
        }).catch((err)=>{
            const error = err.toString();
            const newState = {...this.state, requestState: RequestState.ERROR, error: error}
            this.setState(newState)
        });
    }

    public render() {

        let content;

        const processedData = this.state.data.map((item: AuthorCombinedTrendChartItem)=>{
            const newItem = {...item, proposals_total: item.proposals_total, awards_total: item.awards_total}
            return newItem;
        })

        if(this.state.requestState === RequestState.SUCCESS){
            content = (
                <React.Fragment>
                    <ResponsiveContainer  height='100%' width='100%' >
                        <ComposedChart data={processedData}
                                margin={{top: 20, right: 30, left: 20, bottom: 5}}  >
                            <XAxis dataKey="year" tick={<HorizontalAxisTick fontSize={12} textAnchor={"middle"} tickFormatter={this.horizontalTickFormatter} />} />
                            <YAxis tickFormatter={this.tickFormatter} tick={{fontSize:12} } /> } />
                            <Tooltip content={<CombinedToolTip 
                                                    labelName={"Year"}
                                                    valueLabel = {"Amount"}
                                                    valueFormatter = {this.valueFormatter}
                                                    />}/>
                            <Legend />
                            <Bar dataKey="proposals_total" name={"Proposals Total"} barSize={20} className={css(styles.trend_bar_hover)} fill = "#e1c1a4" >
                                    {
                                        processedData.map(this.buildTrendProposalsBar2)
                                    }
                            </Bar>
                            <Bar dataKey="proposals_pi" name={"Proposals as PI"} barSize={20} className={css(styles.trend_bar_hover)} fill = "#955f26" >
                                    {
                                        processedData.map(this.buildTrendProposalsBar1)
                                    }
                            </Bar>
                            <Bar dataKey="awards_total" name={"Awards Total"} barSize={20} className={css(styles.trend_bar_hover)} fill = "#64b9ee"  >
                                    {
                                        processedData.map(this.buildTrendAwardsBar2)
                                    }
                            </Bar>
                            <Bar dataKey="awards_pi" name={"Awards as PI"} barSize={20} className={css(styles.trend_bar_hover)} fill = "#002456"  >
                                    {
                                        processedData.map(this.buildTrendAwardsBar1)
                                    }
                            </Bar>
                            <Bar dataKey="expenditures" name={"Expenditures"} barSize={20} className={css(styles.trend_bar_hover)} fill = "#edcf2e" >
                                    {
                                        processedData.map(this.buildTrendExpendituresBar)
                                    }
                            </Bar>
                            
                        </ComposedChart>
                    </ResponsiveContainer>  
                </React.Fragment>
            );
        }else if(this.state.requestState === RequestState.LOADING){
            content = (<div style={{marginLeft: "auto", marginRight: "auto"}} >
                Loading
            </div>)
        }else if(this.state.requestState === RequestState.ERROR){
            content = (
                <div style={{marginLeft: "auto", marginRight: "auto"}} >
                    {this.state.error}
                </div>
            )
        }else{
            content = (
                <div>

                </div>
            )
        }

        let startYearDropDownContentStyle;
        if(this.state.isStartYearDialogVisible){
            startYearDropDownContentStyle = {
                display: "block"
            }
        }else{
            startYearDropDownContentStyle = {
                display: "none"
            }
        }

        let endYearDropDownContentStyle;
        if(this.state.isEndYearDialogVisible){
            endYearDropDownContentStyle = {
                display: "block"
            }
        }else{
            endYearDropDownContentStyle = {
                display: "none"
            }
        }
        
        return (
            <Card id="trend_container" className={css(styles.trend_container,styles.card)}>

                <CardHeader id="trend_header" className={css(styles.card_header)} >

                    <div id="trend_label" className={css(styles.card_label)} >
                        Combined trend
                    </div>

                    <div id="range" className={css(styles.range)} >
                        <div id="start_button_container" className={css(styles.dropdown)} onMouseLeave={this.onStartYearMouseLeave} >
                            <Button color="link" className={css(styles.date)} onClick={this.onStartYearClicked} >
                                FY {this.state.startYear}
                            </Button>
                            <div className={css(styles.dropdown_content)} style={startYearDropDownContentStyle}  >
                                <YearPicker 
                                        selectedStartYear={this.state.startYear} 
                                        selectedEndYear={this.state.endYear}
                                        onYearSelected={this.onStartYearSelected} 
                                        isStartYear={true}/>                                
                            </div>
                        </div>  
                        <div>
                            to
                        </div>
                        <div id="end_button_container" className={css(styles.dropdown)} onMouseLeave={this.onEndYearMouseLeave} >
                            <Button color="link" className={css(styles.date)} onClick={this.onEndYearClicked} >
                                FY {this.state.endYear}
                            </Button>
                            <div className={css(styles.dropdown_content)} style={endYearDropDownContentStyle}  >
                                <YearPicker 
                                        selectedStartYear={this.state.startYear}
                                        selectedEndYear={this.state.endYear}
                                        onYearSelected={this.onEndYearSelected}
                                        isStartYear={false} />                                
                            </div>
                        </div>  
                    </div>
                    
                    <div id="icons" className={css(styles.icons)} >
                                                                                 
                            <div onClick={this.onDownloadImageClick} className={css(styles.icon)} title={"Save as image"} >
                                <FontAwesomeIcon icon={faFileImage} />
                            </div>

                            <div onClick={this.onDownloadDataClick} className={css(styles.icon)} title={"Download data"} >
                                <FontAwesomeIcon icon={faDownload} />
                            </div>
                    </div> 

                </CardHeader>
                
                <CardBody id="div_trend_chart" className={css(styles.trend_chart_container)}>
                    {content}
                </CardBody>
            </Card>
        );
    }

    private onEndYearMouseLeave = (event: any) => {
        this.setState({...this.state, isEndYearDialogVisible: false})
    }

    private onEndYearClicked = (event: any) => {
        this.setState({...this.state, isEndYearDialogVisible: !this.state.isEndYearDialogVisible})
    }

    private onEndYearSelected = (year: number) => {
        if(year >= this.state.startYear && year - this.state.startYear <= 10){
            this.setState({...this.state, endYear: year, isEndYearDialogVisible: false})
            this.fetchData(this.state.startYear, year);   
            if(this.props.onRangeChanged){
                this.props.onRangeChanged(this.state.startYear, year);
            }
        }
    }

    private onStartYearMouseLeave = (event: any) => {
        this.setState({...this.state, isStartYearDialogVisible: false})
    }

    private onStartYearClicked = (event: any) => {
        this.setState({...this.state, isStartYearDialogVisible: !this.state.isStartYearDialogVisible})
    }

    private onStartYearSelected = (year: number) => {
        if(year <= this.state.endYear && this.state.endYear - year <= 10){
            this.setState({...this.state, startYear: year, isStartYearDialogVisible: false})
            this.fetchData(year, this.state.endYear);   
            if(this.props.onRangeChanged){
                this.props.onRangeChanged(year, this.state.endYear);
            }
        }
    }

    private onDownloadImageClick = (event: any) => {
        const element = document.getElementById("trend_container");
        if(element !== null){
            domtoimage.toBlob(element)
            .then((blob: any) => {
                fileDownload(blob, `trend_chart.png`);
            });
        }
    }

    private onDownloadDataClick = (event: any) => {
        json2csv(this.state.data,(err: any,csv: any) => {
            fileDownload(csv,"data.csv")
        })
    }

    private buildTrendProposalsBar1 = (entry: AuthorCombinedTrendChartItem,index: number) => {
        const fill = "#955f26"
        return(
            <Cell cursor="pointer" fill={fill}  key={`cell-${index}`}/>
        );
    }

    private buildTrendProposalsBar2 = (entry: AuthorCombinedTrendChartItem,index: number) => {
        const fill = "#e1c1a4"
        return(
            <Cell cursor="pointer" fill={fill}  key={`cell-${index}`}/>
        );
    }

    private buildTrendAwardsBar1 = (entry: AuthorCombinedTrendChartItem,index: number) => {
        const fill = "#002456"
        return(
            <Cell cursor="pointer" fill={fill}  key={`cell-${index}`}/>
        );
    }

    private buildTrendAwardsBar2 = (entry: AuthorCombinedTrendChartItem,index: number) => {
        const fill = "#64b9ee"
        return(
            <Cell cursor="pointer" fill={fill}  key={`cell-${index}`}/>
        );
    }
    
    private buildTrendExpendituresBar = (entry: AuthorCombinedTrendChartItem,index: number) => {
        const fill = "#edcf2e"
        return(
            <Cell cursor="pointer" fill={fill}  key={`cell-${index}`}/>
        );
    }

    private valueFormatter = (value: string) => {
        return "$" + millify(+value);
    }

    private tickFormatter = (value: string) => {
        return "$" + millify(+value);
    }

    private horizontalTickFormatter = (value: string) => {
        return "FY-" + value;
    }

}


// WEBPACK FOOTER //
// ./src/components/charts/CombinedTrendChart.tsx